import Vue from "vue";
import Vuex from "vuex";

import { cloneDeep, filter, forEach, has, head, isEmpty, isNil, map, pullAt, values, isArray } from "lodash-es";

import { default as router, START_LOCATION } from "../router";
import i18n from "../i18n";

import bfcm2023 from "./modules/bfcm2023";
import permissions from "./permissions/personal";
import analytics from "./modules/analytics";
import terms from "./modules/terms";
import stripe from "./modules/stripe";
import spark from "./modules/spark";
import organizations from "./modules/organizations";
import userAgent from "./modules/user-agent";

import { APP_ENV, IS_OVERHAUL_ENABLE, IS_ACCOUNT_USAGE_ENABLED, IS_FREESTAR_ENABLED } from "@Constants/env";
import TFA_TYPE from "@Constants/two-factor-authenticator";
import { logError, logInfo } from "@Helpers/logger";
import ShowModal from "@Helpers/functions/showModal.js";
import XNotifications from "@Modules/shared/store/XNotifications";
import NotificationPopup from "@Components/NotificationPopup.vue";

Vue.use(Vuex);

let teamMode = false;
try {
  teamMode = localStorage.getItem("teamMode") === "true";
} catch (e) {}

let couponData = undefined;
try {
  couponData = JSON.parse(sessionStorage.getItem("coupon"));
} catch (e) {}

export default new Vuex.Store({
  modules: {
    bfcm2023,
    permissions,
    analytics,
    terms,
    stripe,
    spark,
    organizations,
    userAgent,
  },

  state: {
    urls_list: [],
    urls_page: 0,
    urls_next: undefined,
    urls_filter: {
      search: {
        query: "",
        type: "alias",
      },
      state: {
        name: i18n.t("urls.filter.available"),
        icon: "fa-globe",
        action: "available",
      },
    },

    aliasRouteValues: {},
    aliasRouteConditions: {},
    aliasRouteOperations: {},
    aliasRouteUrlsConditions: {},

    vanityDomains: [], // List of domains that users can create subdomains for
    brandedDomains: [], // list of branded domains the user has created
    brandedDomainsLoading: false,

    userSubscriptions: undefined,
    teamSubscriptions: {},
    organizationSubscriptions: {},
    team: {},
    teamMode: teamMode,

    apiTokens: [],
    apiTokenAbilities: [],

    isPriorityNotificationVisible: false,
    loadingNotifications: false,
    notifications: [],
    announcements: [],

    couponData: couponData,

    billingPeriod: "yearly",

    trialBannerPromoClosedAt: sessionStorage.getItem("trialBannerPromoClosedAt"),
    trialBannerReminderClosedAt: sessionStorage.getItem("trialBannerReminderClosedAt"),
    trialModalPromoClosedAt: localStorage.getItem("trialModalPromoClosedAt"),
    trialModalReminderHardClosedAt: localStorage.getItem("trialModalReminderHardClosedAt"),
    trialModalReminderSoftClosedAt: localStorage.getItem("trialModalReminderSoftClosedAt"),
  },

  getters: {
    isDevelop: (state) => APP_ENV === "develop",
    isProduction: (state) => APP_ENV === "production",

    flashMessages: (state, getters) => (getters.isSparkStateLoaded && getters.getSparkState.flashMessages) || {},
    user: (state, getters) => (getters.isSparkStateLoaded && getters.getSparkState.user) || {},

    userCurrency: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.currency,
    userLimits: (state, getters) => (getters.isUserLoggedIn && getters.user.limits) || {},
    userLocale: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.locale,
    userOptions: (state, getters) => (getters.isUserLoggedIn && getters.user.options) || {},
    isDeveloper: (state, getters) => (getters.isUserLoggedIn && getters.user.isDeveloper) || false,

    isImpersonating: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.impersonated,
    teamsAllowed: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.teamsAllowed,
    userVerified: (state, getters) => getters.isUserLoggedIn && getters.user.email_verified_at,
    recaptcha: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.recaptcha,
    antiRobot: (_, getters) => getters.isSparkStateLoaded && getters.getSparkState.captcha,
    locale: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.locale,
    locales: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.locales,
    currency: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.currency,
    commonDomains: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.domains,
    nameservers: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.domains.nameservers,
    ccpaRequired: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.CCPA,
    gdprRequired: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.GDPR,

    teams: (state, getters) => (getters.isUserLoggedIn && getters.teamsAllowed ? getters.user.teams || [] : []),
    currentTeam: (state, getters) => getters.teams.find((team) => team.id === getters.user.current_team_id),
    team: (state, getters) => (getters.teamMode ? getters.currentTeam : undefined),
    teamMode: (state, getters) => (getters.isUserLoggedIn && getters.teamsAllowed && getters.teams.length > 0 ? state.teamMode : false),

    isSurveyEnabled: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.surveyEnabled,
    contactSalesAttachmentsEnabled: (state, getters) => getters.isSparkStateLoaded && getters.getSparkState.contactSales?.attachmentsEnabled,

    isOrganizationSubscribed: (state, getters) => {
      return getters.organization && getters.organization.subscriptions.current && getters.organization.subscriptions.current.length > 0;
    },

    isTeamSubscribed: (state, getters) => {
      return getters.organizationTeam && getters.organizationTeam.subscriptions.current && getters.organizationTeam.subscriptions.current.length > 0;
    },

    isUserDowngradedToFree: (_, getters) => !getters.isUserSubscribed && getters.hasSubscriptionHistory,
    isUserLoggedIn: (_, getters) => getters.isSparkStateLoaded && getters.getSparkState.user !== undefined,
    isUserVerifiedOrSubscribed: (_, getters) => getters.userVerified || getters.isUserSubscribed,
    isUserSubscribed: (_, getters) => {
      const userSubscription = getters.getUserCurrentSubscription;

      return getters.isUserLoggedIn && !isNil(userSubscription) && !userSubscription.isFree;
    },

    userDomains: (_, getters) => (getters.isUserLoggedIn && getters.user.domains.owned) || [],
    userPersonalDomain: (_, getters) => getters.isUserLoggedIn && getters.user.domains.personal,

    teamDomains: (state, getters) => (getters.team && getters.team.domains.owned) || [],
    teamPersonalDomain: (state, getters) => getters.team && getters.team.domains.personal,
    domains: (state, getters) => (getters.teamMode ? getters.teamDomains : getters.userDomains),
    personalDomain: (state, getters) => (getters.teamMode ? getters.teamPersonalDomain : getters.userPersonalDomain),

    availableDomains: (state, getters) => {
      const list = [];

      if (getters.domains && getters.domains.length > 0) {
        list.push(
          ...getters.domains.map((data) => {
            const domain = Object.assign({}, data);
            domain.type = domain.isBranded ? "branded" : "custom";

            return domain;
          }),
        );
      }

      list.push(
        ...cloneDeep(getters.commonDomains.available).map((data) => {
          data.type = "common";
          return data;
        }),
      );

      return list;
    },

    vanityDomains: (state) => state.vanityDomains,

    brandedDomains: (state) => {
      return state.brandedDomains
        .map((domain) => {
          const domainTemp = { ...domain };
          // TODO: include base domain, subdomain, and isTinySub with data during CREATION to avoid repeated calculation
          // TODO: the domain splitting below will fail on test.co.uk & other 2 part top level domains
          const parts = domain.domain.split(".");
          domainTemp.subDomain = parts.slice(0, parts.length - 2).join(".");
          domainTemp.baseDomain = parts.slice(parts.length - 2, parts.length).join(".");

          return domainTemp;
        })
        .sort((d1, d2) => {
          if (d1.isDeleted !== d2.isDeleted) {
            return d1.isDeleted ? 1 : -1;
          }

          return d1.domain.localeCompare(d2.domain);
        })
        .sort((d1, d2) => {
          return new Date(d2.created_at) - new Date(d1.created_at);
        });
    },

    brandedDomainsLoading: (state) => state.brandedDomainsLoading,

    apiTokens: (state) => state.apiTokens,
    apiTokenAbilities: (state) => state.apiTokenAbilities,

    urls: (state) => state.urls_list,
    urlsFilter: (state) => state.urls_filter || {},
    hasUrls: (state) => state.urls_list.length > 0,
    getUrlsCurrentPage: (state) => state.urls_page,
    getUrlsNextPage: (state) => state.urls_next,

    operationCost: () => (operation) => Vue.Spark("operationsCosts." + operation) || 0,

    aliasRouteValues: (state) => state.aliasRouteValues,
    aliasRouteConditions: (state) => state.aliasRouteConditions,
    aliasRouteOperations: (state) => state.aliasRouteOperations,
    aliasRouteUrlsConditions: (state) => state.aliasRouteUrlsConditions,

    userSubscriptions: (state) => state.userSubscriptions || [],
    teamSubscriptions: (state) => (slug) => state.teamSubscriptions[slug] || [],
    organizationsSubscriptions: (state) => (slug) => state.organizationSubscriptions[slug] || [],

    loadingNotifications: (state) => state.loadingNotifications,
    notifications: (state) => state.notifications,

    unreadNotificationsCount: (state) => filter(state.notifications, (notification) => !notification.read).length,
    hasUnreadNotifications: (state, getters) => getters.unreadNotificationsCount > 0,

    announcements: (state) => state.announcements,
    unreadAnnouncementsCount: (state, getters) => {
      if (state.announcements && getters.user) {
        if (
          state.announcements.length &&
          !getters.user.last_read_announcements_at
        ) {
          return state.announcements.length;
        }

        return filter(state.announcements, (announcement) => {
          const user_last_read = moment.utc(getters.user.last_read_announcements_at);
          const announcement_created_date = moment.utc(announcement.created_at);

          return user_last_read.isBefore(announcement_created_date);
        }).length;
      }

      return 0;
    },
    hasUnreadAnnouncements: (state, getters) => getters.unreadAnnouncementsCount > 0,

    isTeamMember(state, getters) {
      if (!getters.team || !getters.isUserLoggedIn) {
        return false;
      }

      return getters.team.users.some((user) => user.id === getters.user.id);
    },

    hasTeamPermission: (state, getters) => (permission) => {
      if (!getters.team || !getters.isUserLoggedIn) {
        return false;
      }

      return getters.team.permissions[permission] || false;
    },

    TFAList: () => {
      const TFAList = [
        {
          type: 1,
          name: "TOTP",
          component: "totp",
          confirmQRCode: true,
        },
      ];

      return TFAList;
    },

    /**
     * Calculates and returns the default TinyURL expiration duration based on the user's or team's
     * subscription settings.
     *
     * @returns {string|null} - The calculated expiration date and time in UTC format ("YYYY-MM-DDTHH:mm")
     * or null if no expiration is set.
     */
    defaultAliasExpirationDuration: (_, getters) => {
      const billable = getters.teamMode ? getters.team : getters.user;
      const expiration = billable?.options?.alias_lifetime;

      if (!expiration) return null;

      const duration = moment.duration(expiration);
      return moment().add(duration).utc().format("YYYY-MM-DDTHH:mm");
    },

    hasBackupCodes: (_, getters) => {
      return getters.user.active_two_factor_backup_codes_count > 0;
    },

    isTwoFactorEnabled: (_, getters) => {
      if (!getters.isUserLoggedIn) return false;
      return getters.user.uses_two_factor_auth !== TFA_TYPE.DISABLED;
    },

    coupon: (state) => {
      return state.couponData;
    },
    couponCode: (state) => {
      return state.couponData instanceof Object ? state.couponData.coupon : null;
    },

    hideInvoices: () => (billable) => {
      return (billable.status & 0x10) === 0x10;
    },

    receiveMailings: () => (billable) => {
      return (billable.status & 0x08) === 0x00;
    },

    isOverhaulEnabled: () => IS_OVERHAUL_ENABLE === "true",

    isAccountUsageEnabled: () => IS_ACCOUNT_USAGE_ENABLED === "true",

    isFreestarEnabled: () => IS_FREESTAR_ENABLED === "true",

    isFreestarLoaded: () => window.freestar || "freestar" in window,

    // check if user has trialed in the past year
    isTrialingAllowed: (state, getters) => (getters.userSubscriptions.filter((s) => s.trialAvailable).length ? true : false),

    trialBannerPromoClosedAt: (state) => state.trialBannerPromoClosedAt,
    trialBannerReminderClosedAt: (state) => state.trialBannerReminderClosedAt,
    trialModalPromoClosedAt: (state) => state.trialModalPromoClosedAt,
    trialModalReminderHardClosedAt: (state) => state.trialModalReminderHardClosedAt,
    trialModalReminderSoftClosedAt: (state) => state.trialModalReminderSoftClosedAt,

    unpaidOverage: (_, getters) => getters.getSparkState?.user?.unpaid_overage || 0,
    hasUnpaidOverage: (_, getters) => getters.unpaidOverage > 0,
  },

  mutations: {
    ADD_URLS(state, data) {
      state.urls_list.push(...data);
    },
    RESET_URLS(state) {
      state.urls_list = [];
      state.urls_page = 0;
      state.urls_next = undefined;
    },
    UPDATE_URL(state, payload) {
      const index = head(pullAt(payload, "index"));

      if (index === undefined || !has(state.urls_list, index)) {
        return;
      }

      forEach(payload, (value, key) => {
        Vue.set(state.urls_list[index], key, value);
      });
    },
    SET_URLS_FILTER(state, payload) {
      state.urls_filter = {
        ...state.urls_filter,
        ...payload,
      };
    },
    SET_URLS_CURRENT_PAGE(state, payload) {
      state.urls_page = payload;
    },
    SET_URLS_NEXT_PAGE(state, payload) {
      state.urls_next = payload;
    },

    setApiTokens(state, payload) {
      state.apiTokens = payload;
    },
    setApiTokenAbilities(state, payload) {
      state.apiTokenAbilities = payload;
    },

    SET_ANNOUNCEMENTS: (state, payload) => (state.announcements = payload),
    SET_NOTIFICATIONS: (state, payload) => (state.notifications = payload),

    SET_USER_SUBSCRIPTIONS: (state, payload) => (state.userSubscriptions = payload),
    SET_TRIAL_CLOSED_AT_VALUES: (state, payload) => (state[payload.state] = payload.timestamp),

    SET_BILLING_PERIOD_TO_YEARLY: (state) => (state.billingPeriod = "yearly"),
    SET_BILLING_PERIOD_TO_MONTHLY: (state) => (state.billingPeriod = "monthly"),

    teamMode(state, teamMode = true) {
      teamMode = Boolean(teamMode);
      if (teamMode !== state.teamMode) {
        state.teamMode = teamMode;
        localStorage.setItem("teamMode", teamMode ? "true" : "false");
      }
    },
  },

  actions: {
    prepareAliasRoutesData({ state }) {
      return new Promise((resolve, reject) => {
        if (!isEmpty(state.aliasRouteValues)) {
          resolve();
        } else {
          axios.get(Vue.apiUrl("routes/init")).then(({ data }) => {
            state.aliasRouteValues = values(
              map(data.values, (value) => ({
                code: value.id,
                label: value.name,
                type: value.type,
                values: value.values || null,
                custom_values: value.custom_values || null,
                is_assoc: value.is_assoc || false,
              })),
            );

            state.aliasRouteConditions = values(
              map(data.conditions, (condition) => ({
                code: condition.id,
                label: condition.name,
                has_operations: condition.has_operations,
              })),
            );

            state.aliasRouteOperations = values(
              map(data.operations, (operation) => ({
                code: operation.id,
                label: operation.name,
                types: operation.types,
                support_list: operation.support_list,
              })),
            );

            state.aliasRouteUrlsConditions = values(
              map(data.urls_conditions, (condition) => ({
                code: condition.id,
                label: condition.name,
              })),
            );

            resolve();
          });
        }
      });
    },

    prepareUserSubscriptions({ commit, getters }) {
      commit("SET_USER_SUBSCRIPTIONS", []);

      const url = Vue.apiUrl("/subscriptions");
      const params = { billing_type: getters.getUserBillingType };

      return axios.get(url, { params }).then(({ data }) => {
        const subscriptions = data.data;

        if (!isArray(subscriptions)) {
          logError("[prepareUserSubscriptions] Impossible to extract subscriptions from response.");

          return;
        }

        commit("SET_USER_SUBSCRIPTIONS", subscriptions);
      });
    },

    prepareTeamSubscriptions({ state }, teamSlug) {
      if (state.teamSubscriptions[teamSlug] === undefined) {
        Vue.set(state.teamSubscriptions, teamSlug, []);

        const url = Vue.apiUrl("/organization/{organization}/team/{team}/subscriptions", {
          team: teamSlug,
        });

        return axios.get(url).then(({ data }) => {
          Vue.set(state.teamSubscriptions, teamSlug, data.data);
        });
      }
    },

    prepareOrganizationSubscriptions({ state }, organizationSlug) {
      if (state.organizationSubscriptions[organizationSlug] === undefined) {
        Vue.set(state.organizationSubscriptions, organizationSlug, []);

        const url = Vue.apiUrl("/organization/{organization}/subscriptions", {
          organization: organizationSlug,
        });

        return axios.get(url).then(({ data }) => {
          Vue.set(state.organizationSubscriptions, organizationSlug, data.data);
        });
      }
    },

    switchTeam({ getters, dispatch, commit }, teamIdentifier) {
      // Using pending route as current on initial navigation
      const currentRoute = router.currentRoute === START_LOCATION ? router.history.pending : router.currentRoute;
      const isTeamRoute = currentRoute.matched.some((record) => record.name === "teams.team");
      const isSettingsRoute = currentRoute.matched.some((record) => record.name === "settings");

      if (teamIdentifier !== undefined) {
        const teamsIdentifiedByPath = Vue.Spark("teamsIdentifiedByPath");
        const currentTeamIdentifier = getters.team ? (teamsIdentifiedByPath ? getters.team.slug : getters.team.id) : undefined;

        if (currentTeamIdentifier !== teamIdentifier) {
          const url = Vue.apiUrl("{teamPrefix}/{teamId}/switch", {
            teamPrefix: Vue.Spark("teamsPrefix"),
            teamId: teamIdentifier,
          });

          return axios
            .get(url)
            .then(() => {
              return dispatch("refreshState").then(() => {
                commit("teamMode", true);
                if (isSettingsRoute) {
                  router.push({
                    name: "teams.team",
                    params: { teamId: teamIdentifier },
                  });
                }
              });
            })
            .catch((error) => {
              if (error.response?.status === 424) {
                window.location.href =
                  router.resolve({
                    name: "teams.team",
                    params: { teamId: teamIdentifier },
                  }).href + "/login";
              }
            });
        } else {
          if (!getters.teamMode && isSettingsRoute) {
            router.push({
              name: "teams.team",
              params: { teamId: teamIdentifier },
            });
          }
          commit("teamMode", true);
        }
      } else {
        commit("teamMode", false);
        if (isTeamRoute) {
          router.push({ name: "settings" });
        }
      }
    },

    getBrandedDomains({ state }) {
      state.brandedDomainsLoading = true;

      axios.get(Vue.apiUrl("settings/branded-domains")).then(({ data }) => {
        state.brandedDomains = data.data;
        state.brandedDomainsLoading = false;
      });
    },

    getVanityDomains({ state }) {
      return axios
        .get(Vue.apiUrl("settings/vanity-subdomains"))
        .then(({ data }) => ( state.vanityDomains = data ));
    },

    loadNotifications({ commit, state, getters }) {
      if (!getters.isUserLoggedIn) {
        return Promise.reject("Not authorized");
      }

      state.loadingNotifications = true;

      return axios
        .get(Vue.apiUrl("notifications/recent"))
        .then(({ data }) => {
          commit("SET_ANNOUNCEMENTS", data.announcements);
          commit("SET_NOTIFICATIONS", data.notifications);

          /**
           * If a notification is considered important then we display it as a popup
           * so that we know that they saw it
           */
          Object.values(data.notifications).forEach((notification) => {
            if (state.isPriorityNotificationVisible) {
              return;
            }

            if (!notification.read && notification.severity > 0) {
              state.isPriorityNotificationVisible = true;

              const props = {
                parsedContent: notification.parsed_body,
                buttonLabel: notification.action_text,
                ctaRoute: notification.action_url,
                // The notification itself, not a copy of it: the store flips
                // read on the object it is given, and this is the one the list
                // holds. The store is resolved here rather than at import time
                // so pinia is already active by the time it is asked for.
                onClose: () => XNotifications().markNotificationAsRead(notification),
              };

              ShowModal(
                NotificationPopup,
                props,
                { classes: ["v--custom-modal"] },
                { "before-close": () => { state.isPriorityNotificationVisible = false; }},
              );
            }
          });
        })
        .catch(() => {})
        .finally(() => {
          state.loadingNotifications = false;
        });
    },

    updateLastReadAnnouncementsTimestamp() {
      return axios.put(Vue.apiUrl("user/last-read-announcements-at"));
    },

    rememberCoupon({ state, getters }, { coupon, percentOff }) {
      if (!getters.isUserLoggedIn) {
        logInfo("Using default coupon information!");

        state.couponData = {
          coupon: coupon ?? state.couponData.coupon,
          percent_off: percentOff ?? state.couponData.percent_off,
        };

        sessionStorage.setItem("coupon", JSON.stringify(state.couponData));

        return;
      }

      return axios
        .get(Vue.apiUrl("coupon/{code}", { code: coupon }))
        .then(({ data }) => {
          state.couponData = {
            coupon: coupon,
            amount_off: data.data.amount_off,
            percent_off: data.data.percent_off,
          };

          sessionStorage.setItem("coupon", JSON.stringify(state.couponData));
        })
        .catch(() => {
          sessionStorage.removeItem("coupon");
        });
    },

    forgetCoupon({ state }) {
      state.couponData = undefined;
      sessionStorage.removeItem("coupon");
    },

    creditsOperation({ getters }, { billable, operation }) {
      return new Promise((resolve, reject) => {
        const creditsLimit = billable ? billable.limits.limit.web : 0;
        const creditsUsed = billable ? billable.limits.used.web : 0;
        const operationCost = getters.operationCost(operation);

        if (creditsLimit !== false && operationCost > 0) {
          const creditsRemain = creditsLimit >= creditsUsed ? creditsLimit - creditsUsed : 0;

          if (creditsRemain >= operationCost) {
            import("@Modals/Confirm.vue")
              .then((module) => module.default)
              .then((Confirm) => {
                Bus.$emit("showModal", Confirm, {
                  message: i18n.t("using_credits", {
                    operationCost: operationCost,
                    count: creditsRemain,
                  }),
                  callback: (confirmed) => (confirmed ? resolve() : reject()),
                });
              });
          } else {
            return reject(i18n.t("not_enough_credits"));
          }
        } else {
          return resolve(creditsLimit, creditsUsed, operationCost);
        }
      });
    },
  },
});
